import React, {useEffect, useState} from 'react';
import {styles} from './styles';
import {Platform, Text, View} from 'react-native';
import Entypo from 'react-native-vector-icons/Entypo';

import {
  CommonActions,
  DrawerActions,
  useLinkBuilder,
} from '@react-navigation/native';

import {DrawerContentScrollView} from '@react-navigation/drawer';
import {LinearGradient} from 'expo-linear-gradient';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {Pressable} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  interpolateColor,
  withTiming,
  withRepeat,
  withSpring,
} from 'react-native-reanimated';
import CCText from '../CCText';
import CCColors from '@/Utils/CCColors';
import {CustomInfoBox} from '../InfoIcon';
import Tooltip from '../Tooltip';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {capitalizeFirstLetter, getLocalStorage} from '@/Utils/CommonUtils';
import {ScreenStack} from '@/navigators/StackConstants';
import {useUserPlanStore, selectIsCsocUser} from '@/Store/userPlanStore';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
function MenuItem({
  icon,
  label,
  focused,
  onPress,
  isMobile,
  disabled,
  tooltipText,
}) {
  var iconName = icon ? icon : 'help';
  iconName = focused ? iconName : `${iconName}-outline`;
  // This animation code is super bug prone!!!
  const bgAnim = useSharedValue(0);
  const bgAnimStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        bgAnim.value,
        [0, 1],
        ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 0.1)'],
      ),
    };
  });

  function toggleBgAnim(value) {
    if (!focused) {
      bgAnim.value = withTiming(value, {duration: 200});
    }
  }

  const pressScale = useSharedValue(1);
  const pressStyle = useAnimatedStyle(() => ({
    transform: [{scale: pressScale.value}],
  }));

  useEffect(() => {
    bgAnim.value = withTiming(focused ? 1 : 0, {duration: 150});
  }, [focused, bgAnim]);

  function onPressWrapper() {
    if (!window.location.pathname.toLowerCase().includes('/classroom'))
      focused = true; // this is important
    onPress();
  }

  const iconNode = (
    <Animated.View
      style={[
        styles.menuIcon,
        !isMobile && bgAnimStyle,
        isMobile && styles.mobileMenuIcon,
        pressStyle,
      ]}>
      {disabled ? (
        <Tooltip
          text={tooltipText}
          children={toggle => {
            const handlePress = () => {
              toggle(true); // Show the tooltip
              setTimeout(() => {
                toggle(false); // Hide the tooltip after 3 seconds
              }, 3000);
            };
            return (
              <Pressable
                onPress={handlePress}
                onHoverIn={toggle}
                onHoverOut={toggle}>
                <View>{<Icon name={iconName} size={30} color="#fff" />}</View>
              </Pressable>
            );
          }}
        />
      ) : (
        <Icon name={iconName} size={30} color="#fff" />
      )}
    </Animated.View>
  );

  return (
    <Pressable
      onPress={onPressWrapper}
      disabled={disabled}
      onHoverIn={() => toggleBgAnim(1)}
      onHoverOut={() => toggleBgAnim(0)}
      onPressIn={() => {
        pressScale.value = withTiming(0.88, {duration: 100});
      }}
      onPressOut={() => {
        pressScale.value = withTiming(1, {duration: 150});
      }}
      children={state => {
        return (
          <View
            style={[
              styles.menuItem,
              isMobile && styles.mobileMenuItem,
              isMobile && focused && styles.mobileMenuItemFocused,
              !isMobile && focused && styles.menuItemFocused,
            ]}>
            {!isMobile && focused && <View style={styles.activeAccent} />}
            {iconNode}
            <CCText
              style={[
                [
                  styles.menuItemText,
                  (label === 'Visualization Trainer' ||
                    label === 'Assessment' ||
                    label === 'Subscription' ||
                    'Tournaments') && {fontSize: 10},
                ],
                [
                  isMobile && styles.mobileMenuItemText,
                  isMobile &&
                    (label === 'Visualization Trainer' ||
                      label === 'Assessment' ||
                      label === 'Subscription') && {fontSize: 19},
                ],
              ]}
              numberOfLines={2}
              allowFontScaling={true}>
              {label}
            </CCText>
          </View>
        );
      }}></Pressable>
  );
}

const getStackRouteName = route => {
  const match = ScreenStack.find(screen => {
    return route.toLowerCase() === screen?.Name.toLowerCase();
  });
  return match?.Name;
};

const PATH_EVENT = 'cc:locationchange';
let historyPatched = false;
function patchHistoryOnce() {
  if (historyPatched || typeof window === 'undefined' || !window.history) {
    return;
  }
  historyPatched = true;
  (['pushState', 'replaceState'] as const).forEach(method => {
    const original = window.history[method];
    if (typeof original !== 'function') return;
    window.history[method] = function patched(...args: any[]) {
      const result = original.apply(this, args as any);
      window.dispatchEvent(new Event(PATH_EVENT));
      return result;
    } as any;
  });
}

function useCurrentPathSegment(): string {
  patchHistoryOnce();
  const read = () =>
    typeof window !== 'undefined' && window.location?.pathname
      ? window.location.pathname.replace(/^\//, '').split('/')[0].toLowerCase()
      : '';
  const [segment, setSegment] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = () => setSegment(read());
    window.addEventListener(PATH_EVENT, update);
    window.addEventListener('popstate', update);
    window.addEventListener('hashchange', update);
    return () => {
      window.removeEventListener(PATH_EVENT, update);
      window.removeEventListener('popstate', update);
      window.removeEventListener('hashchange', update);
    };
  }, []);
  return segment;
}

// Route names, not labels. Dashboard stays first for everyone. Order drives
// the left-menu's "primary" section; everything else that would still
// render collapses into the collapsible "Others" bucket below it.
const CSOC_PRIMARY = [
  'dashboard',
  'resources',
  'courses',
  'playChess',
  'CCTournaments',
  'Clubs',
  'FunChallengesLobby',
  'games',
  'positions',
  'VisualisationTrainer',
  'subscription',
];
const NON_CSOC_PRIMARY = [
  'dashboard',
  'playChess',
  'CCTournaments',
  'Clubs',
  'FunChallengesLobby',
  'games',
  'subscription',
];

function MenuItemList({
  state,
  navigation,
  descriptors,
  isMobile,
  disabled,
  userIsStaff,
  id,
  openingGamePercentage,
  middleGamePercentage,
  endGamePercentage,
  calculationDepthPercentage,
  modifyMainMenu,
  othersExpanded,
  setOthersExpanded,
}) {
  const currentPathSegment = useCurrentPathSegment();
  const activeRouteName = (
    state.routes?.[state.index]?.name || ''
  ).toLowerCase();
  const planDetails = useUserPlanStore(s => s.planDetails);
  // Coaches (cc_csoc_coach row -> summary user.is_coach) get the CSOC menu
  // ordering too — School of Chess / Courses lead their day like students'.
  const isCoachAccount = useLoggedInUserStore(s => !!s.userData?.is_coach);
  const isCsoc = selectIsCsocUser(planDetails) || isCoachAccount;

  const isRouteFocused = route => {
    const urlName = (getStackRouteName(route.name) || '').toLowerCase();
    return currentPathSegment
      ? urlName === currentPathSegment
      : route.name.toLowerCase() === activeRouteName;
  };

  const renderRoute = route => {
    const focused = isRouteFocused(route);

    const onPress = async () => {
      const event = navigation.emit({
        type: 'drawerItemPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!event.defaultPrevented) {
        const label = descriptors[route.key]?.options?.label;
        if (
          Platform.OS === 'web' &&
          window.location.pathname.toLowerCase().includes('/classroom')
        ) {
          const routeName = getStackRouteName(route.name) || 'dashboard';
          const url = `${window.location.origin}/${routeName}`;
          //capitalizeFirstLetter(route.name,)
          window.open(url, '_blank');
          return;
        }

        if (modifyMainMenu) {
          navigation.dispatch({
            ...(focused
              ? DrawerActions.closeDrawer()
              : CommonActions.reset({
                  index: 0,
                  routes: [
                    {
                      name: route.name,
                      params: {
                        user_key: await getLocalStorage('user_key'),
                        user_id: await getLocalStorage('reports_user_id'),
                      },
                    },
                  ],
                })),
            target: state.key,
          });
        } else {
          if (label === 'Assessment') {
            navigation.dispatch({
              ...(focused
                ? DrawerActions.closeDrawer()
                : CommonActions.reset({
                    index: 0,
                    routes: [
                      {
                        name: route.name,
                        params: {
                          id,
                          openingGamePercentage,
                          middleGamePercentage,
                          endGamePercentage,
                          calculationDepthPercentage,
                        },
                      },
                    ],
                  })),
              target: state.key,
            });
          }
          if (label === 'Tournaments') {
            navigation.dispatch({
              ...(focused
                ? DrawerActions.closeDrawer()
                : CommonActions.reset({
                    index: 0,
                    routes: [
                      {
                        name: route.name,
                        params: {
                          from: 'tournament', // ✅ your custom query param
                        },
                      },
                    ],
                  })),
              target: state.key,
            });
          } else {
            navigation.dispatch({
              ...(focused
                ? DrawerActions.closeDrawer()
                : CommonActions.reset({
                    index: 0,
                    routes: [{name: route.name}],
                  })),
              target: state.key,
            });
          }
        }
      }
    };

    // console.log('menu', route)
    const opts = descriptors[route.key].options;
    const label = opts.label ? opts.label : route.name;
    const tooltipText = `${label} tab is disabled till analysis of your games is completed`;
    // let userDetails = getUserDetails();
    // userDetails = JSON.parse(userDetails);
    // const is_staff = userDetails?.is_staff;

    if (label === 'Raise a Ticket' && !isMobile) {
      return null;
    }
    if (
      !opts.hide ||
      (opts?.hide !== undefined &&
        opts?.hide === true &&
        userIsStaff &&
        opts?.icon)
    ) {
      // if (opts?.hide !== undefined && opts?.hide === true && userIsStaff) {
      //   debugger;
      // }
      return (
        <MenuItem
          key={route.key}
          label={label}
          icon={opts.icon}
          focused={focused}
          onPress={onPress}
          isMobile={isMobile}
          disabled={opts.disabled}
          tooltipText={tooltipText}
        />
      );
    }
    return null;
  };

  // Others bucket = every route not in the primary list above. Computed
  // ahead of the coach-view early return (and the Others useState) so hooks
  // stay unconditional regardless of which branch actually renders.
  const primaryNames = isCsoc ? CSOC_PRIMARY : NON_CSOC_PRIMARY;
  const primaryNameSet = new Set(primaryNames.map(name => name.toLowerCase()));
  const otherRoutes = state.routes.filter(
    route => !primaryNameSet.has(route.name.toLowerCase()),
  );
  // Expanded on load iff the active route lives in Others — never hide the
  // item the user is currently on behind a collapsed toggle. othersExpanded is
  // owned by MainMenu, which also drives the toggle via the animated bottom
  // chevron.
  useEffect(() => {
    if (otherRoutes.some(isRouteFocused)) {
      setOthersExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (modifyMainMenu) {
    // Coach view: unchanged flat map in registration order, no reorder/Others.
    return state.routes.map(
      renderRoute,
    ) as React.ReactNode as React.ReactElement;
  }

  const primaryElements = primaryNames
    .map(name =>
      state.routes.find(
        route => route.name.toLowerCase() === name.toLowerCase(),
      ),
    )
    .filter(Boolean)
    .map(renderRoute);

  const otherElements = otherRoutes.map(renderRoute).filter(Boolean);

  return (
    <>
      {primaryElements}
      {othersExpanded && otherElements}
    </>
  ) as React.ReactNode as React.ReactElement;
}

export default function MainMenu(props) {
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(false);
  const [othersExpanded, setOthersExpanded] = React.useState(false);
  const {isMobileOrTab} = useMediaQuery();
  const bounceAnim = useSharedValue(0);

  // Set up the bounce animation
  useEffect(() => {
    bounceAnim.value = withRepeat(
      withSpring(10, {
        damping: 2,
        stiffness: 200,
      }),
      -1, // Repeat indefinitely
      true, // Reverse on every iteration
    );
  }, []);

  const bounceStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: bounceAnim.value,
      },
    ],
  }));

  const handleScroll = event => {
    const yOffset = event.nativeEvent.contentOffset.y;
    const viewportHeight = event.nativeEvent.layoutMeasurement.height;
    const totalContentHeight = event.nativeEvent.contentSize.height;

    const atBottom = yOffset + viewportHeight >= totalContentHeight;

    setIsScrolledToBottom(atBottom);
  };
  return (
    <DrawerContentScrollView
      {...props}
      showsVerticalScrollIndicator={false}
      onScroll={handleScroll}
      scrollEventThrottle={16}>
      <MenuItemList
        {...props}
        othersExpanded={othersExpanded}
        setOthersExpanded={setOthersExpanded}
      />
      {!isScrolledToBottom && (
        <LinearGradient
          colors={['transparent', 'rgba(0,0,0,1)']}
          style={[styles.fadeBottom, isMobileOrTab && styles.fadeBottomMobile]}>
          <Pressable onPress={() => setOthersExpanded(v => !v)}>
            <Animated.View style={bounceStyle}>
              <Entypo
                name={othersExpanded ? 'chevron-up' : 'chevron-down'}
                size={30}
                color="white"
                style={{alignSelf: 'center', marginVertical: 5}}
              />
            </Animated.View>
          </Pressable>
        </LinearGradient>
      )}
    </DrawerContentScrollView>
  );
}
